import type { QuoteFile } from 'paintscout'
import { getLibraryFileKey } from '@paintscout/util/builder'

/**
 * Identity of a tile in the library grid, for multi-select. The library's own key
 * (`s3PublicKey`/`cloudinaryPublicId`) is preferred so the same image can never be selected twice,
 * and `file.key` is the fallback for a tile that isn't a library entry — today only the synthetic
 * company-logo tile the dialog prepends.
 */
export function getSelectionKey(file: QuoteFile): string | undefined {
  return getLibraryFileKey(file) || file?.key
}

/** Adds `key` to the selection, or removes it when it's already there. Selection order is click order. */
export function toggleLibrarySelection(keys: string[], key: string | undefined): string[] {
  if (!key) return keys
  return keys.includes(key) ? keys.filter((existing) => existing !== key) : [...keys, key]
}

/**
 * Maps the selection back to indexes into the *grid's* file array — which can differ from the
 * library's own order, since the dialog prepends the company-logo tile — so the grid only has to
 * know which of its tiles are selected.
 */
export function getSelectedIndexes(files: QuoteFile[], keys: string[]): number[] {
  if (!files?.length || !keys?.length) return []

  return files.reduce<number[]>((indexes, file, index) => {
    const key = getSelectionKey(file)
    if (key && keys.includes(key)) indexes.push(index)
    return indexes
  }, [])
}

/**
 * The selected files, in the order they were clicked rather than grid order — the gallery appends
 * them in that order, so picking 3rd/1st/2nd adds them 3rd/1st/2nd. A key whose file is gone (e.g.
 * deleted from the library while selected) is skipped.
 */
export function getSelectedFiles(files: QuoteFile[], keys: string[]): QuoteFile[] {
  if (!files?.length || !keys?.length) return []

  return keys
    .map((key) => files.find((file) => getSelectionKey(file) === key))
    .filter((file): file is QuoteFile => !!file)
}
